import { useEffect, useRef, useState } from "react";
import animatedIcon from "@/assets/phils-conversation-icon-animated.webp.asset.json";
import staticIcon from "@/assets/phils-conversation-icon-static.webp.asset.json";

type AnimatedBrandIconProps = {
  className?: string;
  active?: boolean;
  priority?: boolean;
};

export function AnimatedBrandIcon({ className, active = true, priority = false }: AnimatedBrandIconProps) {
  const ref = useRef<HTMLPictureElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || !active) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (motion.matches) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => setPlaying(false);
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setPlaying(true);
      // Play one complete shimmer, then settle instead of looping indefinitely.
      timer = setTimeout(stop, 4000);
      observer.disconnect();
    }, { threshold: 0.5 });
    observer.observe(element);
    motion.addEventListener("change", stop);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
      motion.removeEventListener("change", stop);
    };
  }, [active]);

  return (
    <picture ref={ref} className={className}>
      <source media="(prefers-reduced-motion: reduce)" srcSet={staticIcon.url} />
      <img
        src={playing && active ? animatedIcon.url : staticIcon.url}
        alt="Phil's Conversation"
        width={512}
        height={512}
        className="h-full w-full object-contain"
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
      />
    </picture>
  );
}